<template>
  <div class="book-list-form">
    <b-overlay :show="pageLoading || saving" rounded="sm" class="w-100">
      <template #overlay>
        <div class="d-flex justify-content-center mb-3 mt-5">
          <b-spinner
            style="width: 3rem; height: 3rem"
            type="grow"
            variant="primary"
            label="Spinning"
          />
        </div>
      </template>

      <b-row class="mb-2">
        <b-col md="12" class="d-flex align-items-center">
          <b-button
            variant="outline-secondary"
            class="btn-icon mr-2 rounded-circle"
            @click="goBack"
          >
            <feather-icon icon="ArrowLeftIcon" />
          </b-button>
          <h4 class="mb-0">
            {{
              isView
                ? $t("View Book List")
                : isEdit
                  ? $t("Edit Book List")
                  : $t("Add Book List")
            }}
          </h4>
        </b-col>
      </b-row>

      <b-card>
        <div class="bl-form-body">
          <div class="bl-form-block mb-3">
            <h6 class="bl-block-title">{{ $t("Class & Section") }}</h6>
            <div v-if="isView" class="bl-meta-grid">
              <div class="bl-meta-item">
                <span class="bl-meta-label">{{ $t("Class") }}</span>
                <span class="bl-meta-value">{{ displayMeta.className || "—" }}</span>
              </div>
              <div class="bl-meta-item">
                <span class="bl-meta-label">{{ $t("Section") }}</span>
                <span class="bl-meta-value">{{ displayMeta.sectionName || "—" }}</span>
              </div>
              <div class="bl-meta-item">
                <span class="bl-meta-label">{{ $t("Items") }}</span>
                <span class="bl-meta-value">{{ activeFormItemCount }}</span>
              </div>
              <div class="bl-meta-item">
                <span class="bl-meta-label">{{ $t("Total Qty") }}</span>
                <span class="bl-meta-value bl-meta-value-primary">{{ totalItemQty }}</span>
              </div>
            </div>
            <b-row v-else>
              <b-col md="6">
                <b-form-group
                  :label="$t('Select class') + ' *'"
                  class="bl-form-group mb-md-0"
                >
                  <v-select
                    v-model="form.classID"
                    :options="classes"
                    :clearable="false"
                    label="name"
                    :reduce="(val) => val.id"
                    :placeholder="$t('Select class')"
                    :disabled="pageLoading"
                    :append-to-body="true"
                    :calculate-position="dropdownPosition"
                    @input="onFormClassChange"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                  :label="$t('Select section') + ' *'"
                  class="bl-form-group mb-0"
                >
                  <v-select
                    v-model="form.sectionID"
                    :options="formSections"
                    :clearable="false"
                    label="section"
                    :reduce="(val) => val.id"
                    :placeholder="$t('Select section')"
                    :disabled="!form.classID || pageLoading"
                    :append-to-body="true"
                    :calculate-position="dropdownPosition"
                    @input="onFormSectionChange"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <div
            v-if="isView || (form.classID != null && form.sectionID != null)"
            class="bl-items-section"
          >
            <div class="bl-items-header">
              <div class="bl-items-header-text">
                <h6 class="bl-items-title mb-0">{{ $t("Items") }}</h6>
                <small class="bl-items-hint">
                  {{
                    isView
                      ? $t("Grouped by category section")
                      : $t("Select a category to add items. Qty 0 rows are excluded on save.")
                  }}
                </small>
              </div>
              <div class="bl-items-count">
                <span class="bl-items-count-label">{{ $t("Total") }}</span>
                <span class="bl-items-count-value">{{ isView ? totalItemQty : activeFormItemCount }}</span>
              </div>
            </div>

            <div
              v-if="!isView && !pageLoading && categoryOptions.length"
              class="bl-add-category-row"
            >
              <label class="bl-add-category-label">{{ $t("Add category") }}</label>
              <v-select
                v-model="selectedCategoryToAdd"
                :options="categoryOptions"
                label="name"
                :reduce="(val) => val.id"
                :clearable="true"
                :placeholder="$t('Select category to add...')"
                :append-to-body="true"
                :calculate-position="dropdownPosition"
                class="bl-add-category-select"
                @input="onAddCategorySelected"
              />
            </div>

            <div v-if="pageLoading" class="bl-empty-state">
              <b-spinner small variant="primary" />
              <span>{{ $t("Loading") }}...</span>
            </div>
            <div v-else-if="!formItems.length" class="bl-empty-state">
              <feather-icon icon="InboxIcon" size="22" class="text-muted mb-50" />
              <p class="mb-0">
                {{
                  isView
                    ? $t("No records found")
                    : categoryOptions.length
                      ? $t("No items yet. Select a category above to add.")
                      : $t("No records found")
                }}
              </p>
            </div>
            <div v-else class="bl-sections-wrap">
              <div
                v-for="section in formItemSections"
                :key="'form-section-' + section.categoryID"
                class="bl-section-card"
              >
                <div class="bl-section-head">
                  <span class="bl-section-name">{{ section.categoryName }}</span>
                  <div class="bl-section-head-actions">
                    <span class="bl-section-count">
                      {{ isView ? section.sectionQty : section.items.length }}
                    </span>
                    <b-button
                      v-if="!isView"
                      variant="danger"
                      size="sm"
                      class="bl-remove-btn"
                      v-b-tooltip.hover.top
                      :title="$t('Remove category')"
                      @click="removeCategory(section.categoryID)"
                    >
                      <feather-icon icon="XIcon" size="14" />
                    </b-button>
                  </div>
                </div>

                <div class="bl-section-table-wrap">
                  <table class="bl-section-table" :class="{ 'bl-section-table--view': isView }">
                    <thead>
                      <tr>
                        <th>{{ $t("Item") }}</th>
                        <th>{{ $t("Particulars") }}</th>
                        <th class="text-center bl-qty-col">{{ $t("Qty") }}</th>
                        <th v-if="!isView" class="bl-action-col"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in section.items"
                        :key="'form-item-' + row._globalIndex"
                      >
                        <template v-if="isView">
                          <td class="bl-view-cell">{{ row.subCategory || "—" }}</td>
                          <td class="bl-view-cell bl-view-cell-muted">
                            {{ row.particular || "—" }}
                          </td>
                          <td class="text-center">
                            <span class="bl-view-qty">{{ row.qty }}</span>
                          </td>
                        </template>
                        <template v-else>
                          <td>
                            <b-form-input
                              v-model="formItems[row._globalIndex].subCategory"
                              type="text"
                              :placeholder="$t('Item name')"
                              class="bl-field-input"
                            />
                          </td>
                          <td>
                            <b-form-input
                              v-model="formItems[row._globalIndex].particular"
                              type="text"
                              :placeholder="$t('Particulars')"
                              class="bl-field-input"
                            />
                          </td>
                          <td class="text-center">
                            <b-form-input
                              v-model.number="formItems[row._globalIndex].qty"
                              type="number"
                              min="0"
                              class="bl-qty-input"
                            />
                          </td>
                          <td class="text-center">
                            <b-button
                              variant="danger"
                              size="sm"
                              class="bl-remove-btn"
                              v-b-tooltip.hover.top
                              :title="$t('Remove')"
                              @click="removeFormItem(row._globalIndex)"
                            >
                              <feather-icon icon="XIcon" size="14" />
                            </b-button>
                          </td>
                        </template>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="!isView" class="bl-section-footer">
                  <button
                    type="button"
                    class="bl-add-another-btn"
                    @click="addItemToCategory(section.categoryID)"
                  >
                    <span class="bl-add-another-icon">
                      <feather-icon icon="PlusIcon" size="14" />
                    </span>
                    <span>{{ $t("Add another item") }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p v-else-if="!isView" class="bl-form-hint mb-2">
            {{ $t("Select class and section to load categories") }}
          </p>

          <div class="bl-form-footer">
            <b-button
              :variant="isView ? 'primary' : 'outline-secondary'"
              @click="goBack"
            >
              <feather-icon v-if="isView" icon="ArrowLeftIcon" size="16" class="mr-50" />
              {{ isView ? $t("Back") : $t("Cancel") }}
            </b-button>
            <b-button
              v-if="!isView"
              variant="primary"
              :disabled="saving || pageLoading"
              @click="saveBookList"
            >
              <b-spinner v-if="saving" small class="mr-50" />
              <feather-icon v-else icon="SaveIcon" size="16" class="mr-50" />
              {{ isEdit ? $t("Update") : $t("Save") }}
            </b-button>
          </div>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  BSpinner,
  BBadge,
  BOverlay,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import {
  HEAD_OFFICE_CAMPUS_ID,
  buildItemSections,
  buildPayloadItems,
  filterItemsWithQty,
  parseBookListSelected,
} from "./bookListUtils";

export default {
  name: "BookListForm",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BSpinner,
    BBadge,
    BOverlay,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      rights: {},
      isEdit: false,
      isView: false,
      pageLoading: false,
      saving: false,
      classes: [],
      classesData: [],
      formSections: [],
      activeCategories: [],
      displayMeta: {
        className: "",
        sectionName: "",
      },
      form: {
        id: 0,
        classID: null,
        sectionID: null,
      },
      formItems: [],
      selectedCategoryToAdd: null,
    };
  },
  computed: {
    headOfficeCampusID() {
      return HEAD_OFFICE_CAMPUS_ID;
    },
    userID() {
      return (
        this.$store.state.userData.userID ||
        this.$store.state.userData.localID ||
        this.$store.state.userData.userLinkID ||
        0
      );
    },
    activeFormItemCount() {
      return this.formItems.filter((row) => Number(row.qty) > 0).length;
    },
    totalItemQty() {
      return this.formItems.reduce(
        (sum, item) => sum + (Number(item.qty) || 0),
        0
      );
    },
    categoryOptions() {
      return this.activeCategories
        .filter((cat) => cat && cat.id)
        .map((cat) => ({
          id: cat.id,
          name: cat.name,
        }));
    },
    formItemSections() {
      return buildItemSections(
        this.formItems,
        this.categoryOptions,
        this.getCategoryName,
        { activeOnly: this.isView }
      );
    },
  },
  async created() {
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    const parentRouteName = "book-list";
    if (!this.$store.state.rights[parentRouteName]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[parentRouteName];

    this.isEdit = this.$route.name === "book-list-edit";
    this.isView = this.$route.name === "book-list-view";

    if (this.isEdit && !this.rights.edit) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    if (this.isView && !this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    if (!this.isEdit && !this.isView && !this.rights.add) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    if (this.isEdit || this.isView) {
      this.pageLoading = true;
      this.$forceUpdate();
      await this.$nextTick();
    }

    await this.loadClasses();

    if (this.isEdit || this.isView) {
      await this.loadExistingRecord();
    }
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    apiBase() {
      return this.$store.state.domain;
    },

    dbParam() {
      return encodeURIComponent(this.$store.state.userData.db);
    },

    goBack() {
      this.$router.push({ name: "book-list" });
    },

    dropdownPosition(dropdownList, component) {
      dropdownList.classList.add("bl-form-select-dropdown");
      const toggle = component.$refs.toggle;
      if (!toggle) return;
      const rect = toggle.getBoundingClientRect();
      dropdownList.style.setProperty("position", "fixed", "important");
      dropdownList.style.setProperty("top", `${rect.bottom + 4}px`, "important");
      dropdownList.style.setProperty("left", `${rect.left}px`, "important");
      dropdownList.style.setProperty("width", `${rect.width}px`, "important");
    },

    getClassName(classID) {
      const cls = this.classes.find((c) => Number(c.id) === Number(classID));
      return cls ? cls.name : "—";
    },

    getSectionName(classID, sectionID) {
      if (Number(sectionID) === 0) {
        return this.$t("All Sections");
      }
      const classData = this.classesData.find(
        (c) => Number(c.cls?.id) === Number(classID)
      );
      const sec = (classData?.sections || []).find(
        (s) => Number(s.id) === Number(sectionID)
      );
      return sec ? sec.section || sec.name : "—";
    },

    buildSectionOptions(classId) {
      const selected = this.classesData.find(
        (c) => Number(c.cls?.id) === Number(classId)
      );
      const sections = (selected?.sections || [])
        .filter((s) => s && s.id)
        .map((s) => ({
          id: s.id,
          section: s.section || s.name || `Section ${s.id}`,
        }));
      return [{ id: 0, section: this.$t("All Sections") }, ...sections];
    },

    getCategoryName(categoryID) {
      const id = Number(categoryID);
      const match = this.activeCategories.find((cat) => Number(cat.id) === id);
      return match?.name || "—";
    },

    async loadClasses() {
      try {
        const response = await this.get({
          url:
            this.apiBase() +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.headOfficeCampusID +
            "&dID=0",
          token: this.$store.state.userData.token,
        });
        const data = response?.data !== undefined ? response.data : response;
        this.classesData = Array.isArray(data) ? data : [];
        this.classes = this.classesData
          .map((el) => el.cls)
          .filter((cls) => cls && cls.id);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.classesData = [];
      }
    },

    loadSectionsForClass(classId) {
      this.formSections = classId ? this.buildSectionOptions(classId) : [];
    },

    async loadActiveCategories() {
      const url =
        this.apiBase() +
        "BookListCategories/GetByPagination?db=" +
        this.dbParam() +
        "&campusID=" +
        this.headOfficeCampusID +
        "&pageNumber=1&pageSize=1000";
      const response = await this.get({
        url,
        token: this.$store.state.userData.token,
      });
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      this.activeCategories = data.filter((c) => c.isActive !== false);
      return this.activeCategories;
    },

    async loadExistingRecord() {
      try {
        const recordId = this.id || this.$route.params.id;
        const url =
          this.apiBase() +
          "BookList/LoadSelected?db=" +
          this.dbParam() +
          "&campusID=" +
          this.headOfficeCampusID +
          "&id=" +
          recordId;
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        const parsed = parseBookListSelected(response, {
          fallbackItem: { id: recordId },
          getCategoryName: this.getCategoryName,
          getClassName: this.getClassName,
          getSectionName: this.getSectionName,
        });

        this.form = {
          id: parsed.header.id,
          classID: parsed.header.classID,
          sectionID: parsed.header.sectionID,
        };
        this.displayMeta = {
          className: parsed.header.className,
          sectionName: parsed.header.sectionName,
        };
        this.loadSectionsForClass(this.form.classID);
        await this.loadActiveCategories();
        this.formItems = this.isView
          ? filterItemsWithQty(parsed.items)
          : parsed.items;
      } catch (error) {
        console.error("Error loading book list:", error);
        this.$bvToast.toast(this.$t("Failed to load record details."), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        this.goBack();
      } finally {
        this.pageLoading = false;
      }
    },

    onFormClassChange() {
      this.form.sectionID = null;
      this.formItems = [];
      this.loadSectionsForClass(this.form.classID);
    },

    onFormSectionChange() {
      if (this.form.classID != null && this.form.sectionID != null && !this.isEdit) {
        this.loadActiveCategories();
      }
    },

    addItemToCategory(categoryId) {
      if (categoryId == null) return;
      const cat = this.activeCategories.find(
        (c) => Number(c.id) === Number(categoryId)
      );
      if (!cat) return;

      this.formItems.push({
        id: 0,
        categoryID: cat.id,
        categoryName: cat.name,
        particular: "",
        subCategory: "",
        qty: 1,
      });
    },

    onAddCategorySelected(categoryId) {
      this.addItemToCategory(categoryId);
      this.$nextTick(() => {
        this.selectedCategoryToAdd = null;
      });
    },

    removeFormItem(index) {
      if (index < 0 || index >= this.formItems.length) return;
      this.formItems.splice(index, 1);
    },

    removeCategory(categoryId) {
      this.formItems = this.formItems.filter(
        (item) => Number(item.categoryID) !== Number(categoryId)
      );
    },

    async saveBookList() {
      if (this.isEdit ? !this.rights.edit : !this.rights.add) return;
      if (this.form.classID == null || this.form.sectionID == null) {
        this.$bvToast.toast(this.$t("Please fill all required fields!"), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      const items = buildPayloadItems(this.formItems);
      if (!items.length) {
        this.$bvToast.toast(
          this.$t("Please add at least one item with quantity greater than 0."),
          {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      const missingItemName = this.formItems.some(
        (row) => Number(row.qty) > 0 && !String(row.subCategory || "").trim()
      );
      if (missingItemName) {
        this.$bvToast.toast(this.$t("Please enter item name for all rows."), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.saving = true;
      try {
        const url =
          this.apiBase() +
          "BookList/Save?db=" +
          this.dbParam() +
          "&campusID=" +
          this.headOfficeCampusID +
          "&userID=" +
          this.userID;
        const response = await this.post({
          url,
          body: {
            header: {
              id: this.form.id || 0,
              classID: this.form.classID,
              sectionID: Number(this.form.sectionID),
            },
            items,
          },
          token: this.$store.state.userData.token,
          context: this,
        });
        if (response !== false && response !== null) {
          this.goBack();
        }
      } catch (error) {
        console.error("Error saving book list:", error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.book-list-form {
  .card {
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    overflow: visible;
  }
}

.bl-form-body {
  padding: 0.25rem;
}

.bl-form-block {
  padding: 1rem 1.25rem;
  border: 1px solid #e3e6f0;
  border-radius: 8px;
  background: #fff;
}

.bl-block-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #5a5c69;
  margin-bottom: 1rem;
}

.bl-form-group {
  ::v-deep label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #5a5c69;
    margin-bottom: 0.5rem;
  }
}

.bl-items-section {
  border: 1px solid #e3e6f0;
  border-radius: 8px;
  overflow: visible;
  background: #fff;
}

.bl-items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  border-radius: 8px 8px 0 0;

  .bl-items-header-text {
    flex: 1;
    min-width: 0;
  }

  .bl-items-title {
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
  }

  .bl-items-hint {
    display: block;
    margin-top: 0.2rem;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.78rem;
    line-height: 1.35;
  }
}

.bl-items-count {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.65rem 0.35rem 0.75rem;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  flex-shrink: 0;
}

.bl-items-count-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  white-space: nowrap;
}

.bl-items-count-value {
  min-width: 26px;
  height: 26px;
  padding: 0 0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: #7c3aed;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1;
}

.bl-add-category-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e3e6f0;
  background: #fafbfc;
}

.bl-add-category-label {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #5a5c69;
}

.bl-add-category-select {
  width: 100%;
  max-width: 100%;
}

.bl-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: #6c757d;
  font-size: 0.875rem;
  text-align: center;
}

.bl-form-hint {
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border: 1px dashed #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  font-size: 0.875rem;
}

.bl-sections-wrap {
  padding: 1rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.bl-section-card {
  border: 1px solid #e3e6f0;
  border-radius: 8px;
  overflow: visible;
  background: #fafbfc;
}

.bl-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border-bottom: 1px solid #e3e6f0;
  border-radius: 8px 8px 0 0;
}

.bl-section-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #5a5c69;
}

.bl-section-head-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.bl-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.bl-meta-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.bl-meta-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #8a8a9a;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.bl-meta-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #5a5c69;
}

.bl-meta-value-primary {
  color: #7c3aed;
}

.bl-section-table--view {
  .bl-view-cell {
    font-size: 0.875rem;
    font-weight: 600;
    color: #5a5c69;
  }

  .bl-view-cell-muted {
    font-weight: 500;
    color: #8a8a9a;
  }

  .bl-view-qty {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 0.5rem;
    background: #f3f0ff;
    color: #7c3aed;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 700;
  }
}

.bl-section-count {
  min-width: 26px;
  height: 26px;
  padding: 0 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f3f0ff;
  color: #7c3aed;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
}

.bl-section-table-wrap {
  overflow-x: auto;
  background: #fff;
}

.bl-section-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead th {
    background: #f8f9fc;
    color: #5a5c69;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid #e3e6f0;
    white-space: nowrap;
  }

  tbody td {
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid #eef0f4;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  th:first-child,
  td:first-child {
    width: 36%;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 40%;
  }
}

.bl-qty-col {
  width: 90px;
}

.bl-action-col {
  width: 52px;
}

.bl-field-input,
.bl-qty-input {
  height: 38px;
  border: 1px solid #d8d6de;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #5a5c69;
  background: #fff;

  &:focus {
    border-color: #8b5cf6;
    box-shadow: 0 0 0 0.15rem rgba(139, 92, 246, 0.2);
  }
}

.bl-qty-input {
  max-width: 80px;
  margin: 0 auto;
  text-align: center;
  font-weight: 600;
}

.bl-remove-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 2px 6px rgba(220, 53, 69, 0.35);
  }
}

.bl-section-footer {
  display: flex;
  justify-content: flex-start;
  padding: 0.75rem 1rem;
  border-top: 1px solid #e3e6f0;
  background: #fff;
}

.bl-add-another-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #7c3aed;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #6d28d9;

    .bl-add-another-icon {
      transform: scale(1.08);
      box-shadow: 0 2px 8px rgba(139, 92, 246, 0.35);
    }
  }
}

.bl-add-another-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(139, 92, 246, 0.25);
}

.bl-form-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #e3e6f0;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.bl-form-select-dropdown.vs__dropdown-menu {
  z-index: 1050;
  position: fixed !important;
  border-radius: 8px !important;
  border: 1px solid #e3e6f0 !important;
  box-shadow: 0 4px 16px rgba(34, 41, 47, 0.12) !important;
  background: #fff !important;
  max-height: 260px !important;
  overflow-y: auto !important;
  margin-top: 0 !important;
}

.bl-form-select-dropdown .vs__dropdown-option {
  padding: 0.6rem 0.9rem !important;
  color: #5a5c69 !important;

  &--highlight {
    background: #f3f0ff !important;
    color: #7c3aed !important;
  }
}
</style>
